import * as React from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { QuotaUsage } from "../types";

export type QuotaHealthTone = "success" | "warning" | "error";

interface QuotaMetric {
	key: "maps" | "storage" | "viewers" | "admins" | "editors";
	label: string;
	used: number;
	limit: number;
	pct: number;
	usedDisplay: string;
	limitDisplay: string;
}

function formatBytes(bytes: number): string {
	const KB = 1024;
	const MB = 1024 * KB;
	const GB = 1024 * MB;

	if (bytes >= GB) {
		const gbValue = bytes / GB;
		return Number.isInteger(gbValue)
			? `${gbValue.toFixed(0)} GB`
			: `${gbValue.toFixed(1)} GB`;
	}
	if (bytes >= MB) return `${(bytes / MB).toFixed(0)} MB`;
	if (bytes >= KB) return `${(bytes / KB).toFixed(0)} KB`;
	return `${bytes} B`;
}

const safePct = (used: number, limit: number): number => {
	if (limit <= 0) return 0;
	return Math.min(100, Math.round((used / limit) * 100));
};

export function buildQuotaMetrics(quota: QuotaUsage): QuotaMetric[] {
	const viewersLimit = quota.viewersLimit ?? 0;
	return [
		{
			key: "maps",
			label: "Maps",
			used: quota.mapsUsed,
			limit: quota.mapsLimit,
			pct: safePct(quota.mapsUsed, quota.mapsLimit),
			usedDisplay: String(quota.mapsUsed),
			limitDisplay: String(quota.mapsLimit),
		},
		{
			key: "storage",
			label: "Storage",
			used: quota.storageBytesUsed,
			limit: quota.storageBytesLimit,
			pct: safePct(quota.storageBytesUsed, quota.storageBytesLimit),
			usedDisplay: formatBytes(quota.storageBytesUsed),
			limitDisplay: formatBytes(quota.storageBytesLimit),
		},
		{
			key: "viewers",
			label: "Viewers",
			used: quota.viewersUsed,
			limit: viewersLimit,
			pct: quota.isViewersUnlimited
				? 0
				: safePct(quota.viewersUsed, viewersLimit),
			usedDisplay: String(quota.viewersUsed),
			limitDisplay: quota.isViewersUnlimited
				? "Unlimited"
				: String(quota.viewersLimit ?? 0),
		},
		{
			key: "admins",
			label: "Admins",
			used: quota.adminsUsed,
			limit: quota.adminsLimit,
			pct: safePct(quota.adminsUsed, quota.adminsLimit),
			usedDisplay: String(quota.adminsUsed),
			limitDisplay: String(quota.adminsLimit),
		},
		{
			key: "editors",
			label: "Editors",
			used: quota.editorsUsed,
			limit: quota.editorsLimit,
			pct: safePct(quota.editorsUsed, quota.editorsLimit),
			usedDisplay: String(quota.editorsUsed),
			limitDisplay: String(quota.editorsLimit),
		},
	];
}

export function quotaHealthTone(pct: number): QuotaHealthTone {
	if (pct >= 90) return "error";
	if (pct >= 70) return "warning";
	return "success";
}

const TONE_COLOR: Record<QuotaHealthTone, "success" | "warning" | "error"> = {
	success: "success",
	warning: "warning",
	error: "error",
};

export interface QuotaHealthIndicatorProps {
	quota: QuotaUsage;
	width?: number;
}

/**
 * Compact list-cell indicator: worst-of-four bar + percent label, full
 * breakdown on hover via Tooltip. Use in dense tables where row height
 * matters more than detail.
 */
export function QuotaHealthIndicator({
	quota,
	width = 120,
}: QuotaHealthIndicatorProps): React.JSX.Element {
	const metrics = buildQuotaMetrics(quota);
	const worst = metrics.reduce(
		(acc, m) => (m.pct > acc.pct ? m : acc),
		metrics[0]!,
	);
	const tone = quotaHealthTone(worst.pct);

	const tooltip = (
		<Stack
			spacing={0.75}
			sx={{ p: 0.5, minWidth: 180 }}>
			{metrics.map((m) => (
				<Stack
					key={m.key}
					spacing={0.25}>
					<Stack
						direction="row"
						justifyContent="space-between">
						<Typography variant="caption">{m.label}</Typography>
						<Typography variant="caption">
							{m.usedDisplay} / {m.limitDisplay}
						</Typography>
					</Stack>
					<LinearProgress
						variant="determinate"
						value={m.pct}
						color={TONE_COLOR[quotaHealthTone(m.pct)]}
						sx={{ height: 4, borderRadius: 2 }}
					/>
				</Stack>
			))}
		</Stack>
	);

	return (
		<Tooltip
			title={tooltip}
			arrow
			placement="left">
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{ width, cursor: "default" }}>
				<Box sx={{ flex: 1 }}>
					<LinearProgress
						variant="determinate"
						value={worst.pct}
						color={TONE_COLOR[tone]}
						sx={{ height: 6, borderRadius: 2 }}
					/>
				</Box>
				<Typography
					variant="caption"
					sx={{
						minWidth: 32,
						textAlign: "right",
						color: `var(--mui-palette-${tone}-700)`,
						fontWeight: 600,
					}}>
					{worst.pct}%
				</Typography>
			</Stack>
		</Tooltip>
	);
}

export interface QuotaBreakdownProps {
	quota: QuotaUsage;
}

/**
 * Full four-bar breakdown for the per-account details page. Shows label,
 * absolute used/limit, percent, and a colored progress bar per metric.
 */
export function QuotaBreakdown({
	quota,
}: QuotaBreakdownProps): React.JSX.Element {
	const metrics = buildQuotaMetrics(quota);
	return (
		<Stack spacing={2}>
			{metrics.map((m) => {
				const tone = quotaHealthTone(m.pct);
				return (
					<Stack
						key={m.key}
						spacing={0.75}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="baseline">
							<Typography variant="body2">{m.label}</Typography>
							<Stack
								direction="row"
								spacing={1.5}
								alignItems="baseline">
								<Typography
									variant="body2"
									color="text.primary">
									{m.usedDisplay} / {m.limitDisplay}
								</Typography>
								<Typography
									variant="caption"
									sx={{
										color: `var(--mui-palette-${tone}-700)`,
										fontWeight: 600,
										minWidth: 36,
										textAlign: "right",
									}}>
									{m.pct}%
								</Typography>
							</Stack>
						</Stack>
						<LinearProgress
							variant="determinate"
							value={m.pct}
							color={TONE_COLOR[tone]}
							sx={{ height: 6, borderRadius: 2 }}
						/>
					</Stack>
				);
			})}
		</Stack>
	);
}
